import { Divider, Flex } from "@chakra-ui/react";
import { isNil } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";

import { AutoRefillToggle } from "components/AutoRefillToggle";
import { CountryComponentWrapper } from "components/Common/CountryComponentWrapper";
import { SummaryItemWrapper } from "components/PrescriptionFlow/TreatmentSummaryContent/SummaryItemWrapper";
import { PRODUCT_TYPES, TAX_PERCENT } from "components/constants";
import { OFIconButton } from "design_system/components/IconButton";
import { EditIcon, TrashcanIcon } from "design_system/icons";
import {
  CalculateOrderSummaryModel,
  OrderStatus,
  ProductNameType,
  RegionType,
  StripePriceType
} from "graphql/generated";
import { colors } from "src/colors";
import { CURRENT_COUNTRY } from "src/constants/Countries";
import { URL_EDIT_PRICE } from "src/constants/url.constants";
import { useConfig } from "src/hooks";
import { getBasePrice, getTax } from "src/utils";
import { checkCountry } from "src/utils/checkCountry";
import { formatPrice } from "src/utils/priceFormat";

import InputCounter from "../InputCounter";
import { OrderSummaryItemHeader } from "./OrderSummaryItemHeader";
import { OrderSummaryItemWrapper } from "./OrderSummaryItemWrapper";
import { PriceSwitchRow } from "./PriceSwitchRow";

interface Props {
  t: (key: string, values?: any) => string;
  item: any;
  summary: CalculateOrderSummaryModel;
  onOpenCancelModal: (item: any) => void;
  onTogglePriceClick: (item: any) => void;
  onToggleAutoRenew: (item: any) => void;
  onQuantityChange: (item: any) => void;
  onEditPriceClick: (item: any) => void;
  allowAction?: boolean;
}

const OTCItem = ({
  onTogglePriceClick,
  onToggleAutoRenew,
  onQuantityChange,
  onOpenCancelModal,
  item,
  summary,
  t,
  allowAction
}: Props) => {
  const { price, product, type, oldOrder } = item;

  const router = useRouter();
  const { addSuffixCountry } = useConfig();
  const { requires_prescription: requiresPrescription } = product.metadata;
  const isRecurringPrice = price?.type === StripePriceType.Recurring;

  const showQuantityInput = useMemo(() => {
    if (product.uniqName === ProductNameType.FertilityHormoneTestKit) {
      return false;
    }

    if (!allowAction) {
      return false;
    }

    return type === StripePriceType.OneTime.toUpperCase() && !requiresPrescription;
  }, [allowAction, product.uniqName, requiresPrescription, type]);

  const showEditButton = useMemo(() => {
    if (product.uniqName === ProductNameType.FertilityHormoneTestKit) {
      return false;
    }

    if (oldOrder?.subId && oldOrder?.status === OrderStatus.Active) {
      return false;
    }

    if (showQuantityInput) {
      return false;
    }

    return true;
  }, [product.uniqName, oldOrder?.subId, oldOrder?.status, showQuantityInput]);

  const handleSelectPrice = useCallback(() => {
    router.push(URL_EDIT_PRICE(item.product.id, item.price.id));
  }, [item, router]);

  const priceItemSummary = summary?.itemList?.find((it) => it?.priceId === price?.id);

  const subTotal = priceItemSummary?.itemSubTotalAfterDiscount || 0;

  return (
    <OrderSummaryItemWrapper key={item.price.id}>
      <OrderSummaryItemHeader item={item} />

      {allowAction && (
        <>
          {type === PRODUCT_TYPES.BOTH && !isNil(item?.isSubscriptionPrice) && (
            <PriceSwitchRow item={item} onTogglePriceClick={onTogglePriceClick} />
          )}

          {isRecurringPrice && (
            <AutoRefillToggle
              mb="16px"
              isAutoRefill={item?.isAutoRenewal}
              onToggle={() => onToggleAutoRenew(item)}
            />
          )}
        </>
      )}
      {showQuantityInput && <InputCounter onQuantityChange={onQuantityChange} {...item} />}
      <Flex flexDirection="column" bgColor={colors.grey[200]} p={2} borderRadius="sm" mt={4}>
        <SummaryItemWrapper
          titleTranslation={t("product-price")}
          value={formatPrice(getBasePrice(subTotal))}
          mb={2}
        />

        <CountryComponentWrapper countries={[RegionType.Sg, RegionType.Jp]}>
          <SummaryItemWrapper
            titleTranslation={
              checkCountry([RegionType.Sg])
                ? t("gst-fee", { tax_percent: TAX_PERCENT[CURRENT_COUNTRY] })
                : t("vat-fee", { tax_percent: TAX_PERCENT[CURRENT_COUNTRY] })
            }
            dataTranslation={formatPrice(getTax(subTotal))}
            mb={1}
          />
        </CountryComponentWrapper>
        <Divider borderColor={colors.grey[500]} borderWidth="1px" my="16px" />
        <SummaryItemWrapper
          value={formatPrice(subTotal)}
          titleTranslation={t(`${addSuffixCountry("checkout")}:due-now`)}
          textStyle="body1"
        />
      </Flex>

      {allowAction && (
        <>
          <Divider m="16px 0px 8px 0px" borderColor={colors.grey[500]} />

          <Flex w="100%" justifyContent="flex-end" gap="16px">
            <OFIconButton icon={<TrashcanIcon />} onClick={() => onOpenCancelModal(item)} />

            {showEditButton && <OFIconButton icon={<EditIcon />} onClick={handleSelectPrice} />}
          </Flex>
        </>
      )}
    </OrderSummaryItemWrapper>
  );
};

export default OTCItem;
